// Wallet API for DIOS10
import apiClient from './client';
import { API_ENDPOINTS } from './config';

// For deposit/withdraw the backend expects Bearer user_<id>. If we're logged in as admin (admin_X)
// but have a linked player in localStorage, send user_<id> so the request is accepted.
function getWalletAuthHeaders() {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
    const userJson = typeof localStorage !== 'undefined' ? localStorage.getItem('user') : null;
    let authToken = token;
    if (token && token.startsWith('admin_') && userJson) {
        try {
            const user = JSON.parse(userJson);
            const playerId = user?.linkedUserId ?? (user?.isAdmin ? null : user?.id);
            if (playerId) authToken = `user_${playerId}`;
        } catch (_) {}
    }
    return authToken ? { Authorization: `Bearer ${authToken}` } : {};
}

// Rate limiter lato client per prevenire chiamate duplicate
const balanceCallCache = new Map();
const BALANCE_CALL_COOLDOWN = 20000; // 20 secondi tra chiamate per lo stesso userId

export const walletApi = {
    // Get wallet balance - con rate limiting lato client
    getBalance: async (userId, options = {}) => {
        const force = options?.force === true;
        const cacheKey = `balance_${userId}`;
        const now = Date.now();
        const lastCall = balanceCallCache.get(cacheKey);
        
        // Se è passato meno di 20 secondi dall'ultima chiamata, blocca
        if (!force && lastCall && (now - lastCall) < BALANCE_CALL_COOLDOWN) {
            const timeRemaining = Math.ceil((BALANCE_CALL_COOLDOWN - (now - lastCall)) / 1000);
            // Blocca silenziosamente - non loggare per evitare spam
            throw new Error(`RATE_LIMIT:${timeRemaining}`);
        }
        
        // Aggiorna il timestamp
        balanceCallCache.set(cacheKey, now);
        
        try {
            const response = await apiClient.get(API_ENDPOINTS.WALLET.BALANCE(userId));
            return response;
        } catch (error) {
            // Se è un errore 429 o rate limit, mantieni il cache
            if (!force && (error.response?.status === 429 || error.message?.startsWith('RATE_LIMIT:'))) {
                // Mantieni il cache per forzare il cooldown
                return;
            }
            // Rimuovi il cache solo per errori non-rate-limit
            balanceCallCache.delete(cacheKey);
            throw error;
        }
    },

    // Deposit funds (uses user token for auth; fallback for admin login with linked player)
    deposit: async (depositData) => {
        const amount = depositData?.amount ?? depositData?.value;
        const currency = depositData?.currency || 'USDT';
        const transactionHash = depositData?.transactionHash ?? depositData?.transaction_hash;
        if (amount == null || amount === '' || !currency) {
            throw new Error('Deposit requires amount and currency');
        }
        const promoCode = depositData?.promoCode?.trim() || undefined;
        const payload = { amount: Number(amount), currency, transactionHash: transactionHash || undefined, promoCode };
        try {
            const response = await apiClient.post(API_ENDPOINTS.WALLET.DEPOSIT, payload, {
                headers: getWalletAuthHeaders()
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Withdraw funds
    withdraw: async (withdrawData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.WALLET.WITHDRAW, withdrawData, {
                headers: getWalletAuthHeaders()
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Get transaction history
    getTransactions: async (userId, limit = 50) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.WALLET.TRANSACTIONS(userId), {
                params: { limit }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Check withdrawal eligibility
    canWithdraw: async (userId) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.WALLET.CAN_WITHDRAW(userId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Get EUR/USD rate for USDT → DIOS conversion (dynamic; backend may still use DIOS10)
    getEurUsdRate: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.WALLET.EUR_USD_RATE);
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Get USDT contract address and target wallet for BSC deposits (used for MetaMask transfer)
    getUsdtAddress: async (network = 'mainnet') => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.WALLET.USDT_ADDRESS, {
                params: { network }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Initiate Wise EUR deposit: returns IBAN, reference, etc. for bank transfer
    previewPromoCode: async (code, amount) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.WALLET.PROMO_PREVIEW, {
                params: { code, amount },
                headers: getWalletAuthHeaders()
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    initiateWiseDeposit: async (userId, amount, promoCode) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.WALLET.WISE_DEPOSIT_INITIATE, {
                userId,
                amount,
                promoCode: promoCode?.trim() || undefined
            }, {
                headers: getWalletAuthHeaders()
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    initiatePaypalDeposit: async (userId, amount, promoCode) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.WALLET.PAYPAL_DEPOSIT_INITIATE, {
                userId,
                amount,
                promoCode: promoCode?.trim() || undefined
            }, {
                headers: getWalletAuthHeaders()
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Initiate Stripe EUR deposit: returns clientSecret for Stripe Elements
    initiateEurDeposit: async (userId, amount, promoCode) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.WALLET.EUR_DEPOSIT_INITIATE, {
                userId,
                amount,
                promoCode: promoCode?.trim() || undefined
            }, {
                headers: getWalletAuthHeaders()
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Get EUR deposit config (e.g. Stripe publishable key) from backend
    getEurDepositConfig: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.WALLET.EUR_DEPOSIT_CONFIG);
            return response;
        } catch (error) {
            throw error;
        }
    }
};

export default walletApi;
